import { Controller } from "stimulus";

// Labels read "NL — Netherlands": a code, a separator, a name.
const SEPARATOR = " — ";

const split = (text) => {
  const label = text || "";
  const at = label.indexOf(SEPARATOR);
  // No separator: the placeholder country, whose label is its name alone.
  if (at === -1) return { code: "", name: label.toLowerCase() };

  return {
    code: label.slice(0, at).toLowerCase(),
    name: label.slice(at + SEPARATOR.length).toLowerCase(),
  };
};

// Which tier a match belongs in. Lower sorts first.
//
// SHP-18850: select2's default is a plain substring match that keeps the list's
// alphabetical order, so typing "NL" offered Finland (fi-nl-and) and Greenland
// (gree-nl-and) before the Netherlands, and "DE" put Germany fifth of seven.
//
// Code and name are ranked separately rather than by where the term appears in
// the whole label. Position alone ties a code match with a name match and then
// breaks the tie on the label — which begins with the code — so searching "Neth"
// would put Netherlands Antilles above the Netherlands.
const tier = ({ code, name }, term) => {
  if (code === term) return 0;
  if (code.startsWith(term)) return 1;
  if (name.startsWith(term)) return 2;

  return 3;
};

export default class extends Controller {
  connect() {
    // Opt-in: this controller is shared by selects that are not lists of codes,
    // and re-ordering their results is not this change's business.
    const rankByCode = this.element.dataset.rankByCode === "true";

    $(this.element)
      .select2({
        width: "100%",
        allowClear: true,
        placeholder: this.element.dataset.placeholder || "",
        ...(rankByCode ? { matcher: this.matcher, sorter: this.sorter } : {}),
      })
      .on("select2:select select2:unselect select2:clear", () => {
        this.element.dispatchEvent(new Event("change", { bubbles: true }));
      });
  }

  // The same case-insensitive substring test select2 applies by default, so the
  // search still finds everything it did. It also records the term, which
  // select2 hands to the matcher but not to the sorter.
  matcher = (params, data) => {
    this.term = (params.term || "").trim().toLowerCase();
    if (this.term === "") return data;

    return (data.text || "").toLowerCase().includes(this.term) ? data : null;
  };

  sorter = (data) => {
    if (!this.term) return data;

    const ranked = data.map((item) => ({ item, parts: split(item.text) }));

    return ranked
      .sort(
        (a, b) =>
          tier(a.parts, this.term) - tier(b.parts, this.term) ||
          a.parts.name.localeCompare(b.parts.name)
      )
      .map(({ item }) => item);
  };
}
